import { useCallback } from "react";
import { useFaust } from "../../contexts/FaustContext";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Circle, Save } from "lucide-react";
import { cn } from "../../lib/utils";

export function RecorderControls() {
    const { state, dispatch, recorderRef } = useFaust();

    const toggleRecord = useCallback(() => {
        const recorder = recorderRef.current;
        if (state.recorderEnabled) {
            recorder.enabled = false;
            dispatch({ type: "SET_RECORDER_ENABLED", value: false });
        } else {
            recorder.enabled = true;
            dispatch({ type: "SET_RECORDER_ENABLED", value: true });
        }
    }, [state.recorderEnabled, recorderRef, dispatch]);

    const handleSave = useCallback(async () => {
        const recorder = recorderRef.current;
        if (recorder.totalSec === 0) return;
        const encoded = await recorder.encode();
        const blob = new Blob([encoded], { type: "audio/wav" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "recording.wav";
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
    }, [recorderRef]);

    return (
        <div className="flex items-center rounded overflow-hidden border border-zinc-800">
            {/* Record button */}
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                            "h-8 w-8 rounded-none border-r border-zinc-800",
                            state.recorderEnabled
                                ? "text-red-500 bg-red-500/10 hover:bg-red-500/20"
                                : "text-zinc-400 hover:text-zinc-200"
                        )}
                        onClick={toggleRecord}
                    >
                        <Circle
                            className={cn(
                                "h-3 w-3",
                                state.recorderEnabled && "fill-red-500 animate-pulse"
                            )}
                        />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                    {state.recorderEnabled ? "Stop Recording" : "Start Recording"}
                </TooltipContent>
            </Tooltip>

            {/* Time display */}
            <span className="flex-1 text-center text-xs font-mono text-zinc-400 px-2 tabular-nums">
                {state.recorderTime}
            </span>

            {/* Save button */}
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-none border-l border-zinc-800 text-zinc-400 hover:text-zinc-200"
                        onClick={handleSave}
                    >
                        <Save className="h-3 w-3" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="left">Save recording</TooltipContent>
            </Tooltip>
        </div>
    );
}
